import React from 'react';
import Head from 'next/head';
import { getRobotsDefinition } from 'helpers/getRobotsDefinition';
import { FilterableQueryParams } from 'models/FilterableQueryParams';
import { normalizeShopSearchRawQuery } from 'helpers/normalizeShopSearchRawQuery';
import { determineProtocol } from 'helpers/url-mapper';
import { useIntl } from 'react-intl';
import { buildRobotoFontCSS } from 'helpers/buildRobotoFontCSS';
import { buildCanonicalHref } from 'helpers/buildCanonicalHref';
import { buildAlternateLinks } from 'helpers/buildAlternateLinks';
import { ShopSearchHeadProps } from 'models/ShopSearchHeadProps';

const ShopSearchHead = ({
  category,
  keyword,
  account,
  totalShopCount,
  queryParams,
  pageLinks,
  host
}: ShopSearchHeadProps) => {
  const normalizedFromQuery = normalizeShopSearchRawQuery(queryParams);
  const metaRobotsContent = getRobotsDefinition(
    normalizedFromQuery,
    totalShopCount
  );
  const { formatMessage } = useIntl();

  const activeFilters: FilterableQueryParams = {
    ...normalizedFromQuery,
    category: category?.urlPath ?? normalizedFromQuery.category,
    keyword: keyword?.urlPath ?? normalizedFromQuery.keyword
  };

  const formattedMessageValues = {
    category: category?.name,
    keyword: keyword?.name,
    account,
    count: totalShopCount
  };
  const windowTitle = formatMessage(
    {
      id: activeFilters.keyword
        ? 'WINDOW_TITLE_KEYWORD'
        : activeFilters.account
          ? 'WINDOW_TITLE_ACCOUNT'
          : activeFilters.q
            ? 'WINDOW_TITLE_QUERY'
            : activeFilters.category
              ? 'WINDOW_TITLE_CATEGORY'
              : 'WINDOW_TITLE'
    },
    formattedMessageValues
  );

  const canonicalHref = buildCanonicalHref(activeFilters, {
    host,
    protocol: determineProtocol()
  });

  const metaDescriptionMessageId = activeFilters.keyword
    ? 'META_DESCRIPTION_KEYWORD'
    : account
      ? 'META_DESCRIPTION_ACCOUNT'
      : category
        ? 'META_DESCRIPTION_CATEGORY'
        : 'META_DESCRIPTION';

  const displayMetaDescription = !activeFilters.q;

  return (
    <Head>
      <title>{windowTitle}</title>
      <meta name="robots" content={metaRobotsContent} />
      {displayMetaDescription && (
        <meta
          name="description"
          content={formatMessage(
            { id: metaDescriptionMessageId },
            formattedMessageValues
          )}
        ></meta>
      )}
      <link rel="canonical" href={canonicalHref} />
      {buildAlternateLinks(pageLinks)}
      <style dangerouslySetInnerHTML={{ __html: buildRobotoFontCSS() }} />
    </Head>
  );
};

export default ShopSearchHead;
